import { Box, Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, useToast } from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IUserType,
  useUserDetailsData
} from '../UserRegistrationSteps/StepThree/signupHook';
import ProfileDetails from '../UserRegistrationSteps/StepThree/ProfileDetails';
import { useUpdatedUserInfoMutation } from 'src/api/signUpMutation';
import { CustomErrorMessage } from '../UserRegistrationSteps/StepThree/StepThree';
import SessionUpdateModal from './SessionUpdateModal';
import { useKeycloak } from '../AuthHOC';
import { PhoneFieldData } from '../FormField/common/PhoneNumberInput';
import { IUserProfileDetailsType } from './ProfileDetails';

export type IEditProfileForm = {
  profileDetails: {
    linkedinProfileUrl?: string;
    phoneNumber?: PhoneFieldData;
  };
  firstName: string;
  lastName: string;
  middleName: string;
  prefix: string;
  secondaryEmail?: string;
  ccEmail?: string;
  isSameAsSecondaryEmail?: boolean;
  additionalEmails?: Array<{ email: string }>;
  orcid?: string;
  degree?: string;
  email?: string;
};

const EditProfileModal = ({
  organizationId,
  data
}: {
  organizationId: string;
  data: { user: IUserType; profileDetails: IUserProfileDetailsType };
}) => {
  const updatedProfileInfoMutation = useUpdatedUserInfoMutation();
  const { logout } = useKeycloak();

  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);

  const toast = useToast();
  const { configs } = useUserDetailsData(organizationId);
  const methods = useForm<IEditProfileForm>({
    defaultValues: {},
    mode: 'onChange',
    reValidateMode: 'onBlur'
  });

  const [isOpen, setIsOpen] = useState(false);
  const [customErrorMessage, setCustomErrorMessage] =
    useState<CustomErrorMessage>({
      dateOfBirth: '',
      orcid: false
    });

  const { getValues, handleSubmit, reset } = methods;

  const onClose = () => {
    setIsOpen(false);
    data && setDefaultData();
    setCustomErrorMessage({ dateOfBirth: '' });
  };

  const onCloseConfirmationModal = () => {
    setIsOpenConfirmationModal(false);
    setIsOpen(false);
  };

  const handleEditProfileSubmit = async () => {
    const isFormHasCustomError =
      customErrorMessage?.dateOfBirth || customErrorMessage.orcid;
    if (isFormHasCustomError) return;

    const values = getValues();
    delete values.isSameAsSecondaryEmail;

    const oldFirstName = data.user?.firstName;
    const oldMiddleName = data.user?.middleName;
    const oldLastName = data.user?.lastName;
    const oldPrefix = data.user?.prefix;
    const phoneNumberValue =
      values.profileDetails?.phoneNumber?.value?.trim() ?? '';
    const countryCallingCode = phoneNumberValue
      ? values.profileDetails?.phoneNumber?.countryCallingCode ?? ''
      : '';
    const countryCode = phoneNumberValue
      ? values.profileDetails?.phoneNumber?.country ?? ''
      : '';

    const payload = {
      section: 'PERSONAL_INFO',
      ...values,
      email: data?.user?.email || '',
      additionalEmails:
        values.additionalEmails
          ?.map(item => item.email?.trim())
          .filter(Boolean) ?? [],
      profileDetails: {
        phoneNumber: phoneNumberValue,
        countryCallingCode,
        countryCode,
        linkedinProfileUrl: values?.profileDetails?.linkedinProfileUrl || ''
      }
    };

    const response = await updatedProfileInfoMutation.mutateAsync({
      payload
    });

    if (!response.isSuccess) {
      const message = response?.message?.details?.length
        ? response.message.details[0]?.message
        : response?.message?.message || 'Internal Server Error';

      toast({
        variant: 'error',
        title: 'Error',
        description: message,
        isClosable: true
      });
    } else {
      toast({
        variant: 'success',
        title: 'Success',
        description: response?.message ?? 'Internal Server Error',
        isClosable: true
      });

      const { firstName, lastName, middleName, prefix } = values;
      if (
        oldFirstName !== firstName ||
        oldMiddleName !== middleName ||
        oldLastName !== lastName ||
        oldPrefix !== prefix
      ) {
        setIsOpenConfirmationModal(true);
      } else {
        setIsOpen(false);
      }
    }
  };

  const setDefaultData = () => {
    const { profileDetails, user } = data;
    const phoneNumber = profileDetails?.phoneNumber || '';
    const countryCallingCode = profileDetails?.countryCallingCode || '';

    const savedCountryCode = profileDetails?.countryCode || '';

    let normalizedPhoneNumber: PhoneFieldData | undefined;

    if (phoneNumber || countryCallingCode || savedCountryCode) {
      normalizedPhoneNumber = {
        country: savedCountryCode,
        countryCallingCode,
        value: phoneNumber,
        fullNumber: phoneNumber
      };
    }

    reset({
      profileDetails: {
        ...profileDetails,
        phoneNumber: normalizedPhoneNumber
      },
      prefix: user?.prefix,
      firstName: user?.firstName,
      middleName: user.middleName,
      lastName: user?.lastName,
      orcid: user?.orcid ?? '',
      degree: user?.degree ?? '',
      secondaryEmail: user?.secondaryEmail ?? '',
      isSameAsSecondaryEmail: user?.isSameAsSecondaryEmail ?? false,
      ccEmail: user?.ccEmail ?? '',
      additionalEmails: user?.additionalEmails?.map(email => ({ email })) ?? []
    });
  };

  useEffect(() => {
    data && setDefaultData();
  }, [data, reset]);

  return (
    <>
      <SessionUpdateModal
        isOpen={isOpenConfirmationModal}
        onClose={() => {}}
        message={
          <>
            Your personal information has been updated successfully. <br />
            The changes will take effect the next time you log in. <br />
            Would you like to re-login now to apply them immediately?
          </>
        }
        actionButton={
          <Flex justifyContent={'flex-end'} gap={3} mt={3}>
            <Button variant="secondary" onClick={onCloseConfirmationModal}>
              Exit
            </Button>
            <Button onClick={() => logout()}>Re-login</Button>
          </Flex>
        }
      />
      <Button variant="secondary" onClick={() => setIsOpen(true)}>
        <Box>
          <FontAwesomeIcon icon={faEdit} />
        </Box>{' '}
        <Text> Edit Profile Details</Text>
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        closeButtonProps={{
          visibility: 'hidden'
        }}
        width="900px"
      >
        <FormProvider {...methods}>
          <Flex direction={'column'} px={8} pt={8}>
            <Flex direction={'column'} gap={3}>
              <Box>
                <FontAwesomeIcon icon={faEdit} size="2xl" />
              </Box>
              <Text textStyle="headingGroup">Edit Profile Details</Text>
              <form
                autoComplete="off"
                onSubmit={handleSubmit(handleEditProfileSubmit)}
              >
                <Flex direction={'column'} gap={5}>
                  <ProfileDetails
                    configs={configs}
                    setCustomErrorMessage={setCustomErrorMessage}
                    customErrorMessage={customErrorMessage}
                    primaryEmail=""
                    showAdditionalEmails
                  />
                  <Flex justifyContent={'flex-end'} gap={3} mb={5}>
                    <Button variant="secondary" onClick={onClose}>
                      Exit Without Saving
                    </Button>
                    <Button
                      type="submit"
                      disabled={updatedProfileInfoMutation?.isLoading}
                    >
                      Save Changes
                    </Button>
                  </Flex>
                </Flex>
              </form>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};

export default EditProfileModal;
